import { Editor as EditorRef } from "@tinymce/tinymce-react";
import {
  prosodyAttributes,
  SSMLTag,
  ssmlTagsAndAttributes,
} from "shared/constants";
import {
  PresetValueProps,
  presetBreakValues,
  presetPitchValues,
  presetRateValues,
  CheckInvalidNestedTagsProps,
  SelectedContourValueProps,
} from "shared/models";

const titleCase = (name: string): string => {
  return name[0].toUpperCase() + name.slice(1).toLowerCase();
};

export { titleCase };

export function getPresetBreakValue(value: string): PresetValueProps {
  return <PresetValueProps>(
    presetBreakValues.find((preset) => preset.value === value)
  );
}

export function getPresetPitchValue(value: string): PresetValueProps {
  return <PresetValueProps>(
    presetPitchValues.find((preset) => preset.value === value)
  );
}

export function getPresetRateValues(value: string): PresetValueProps {
  return <PresetValueProps>(
    presetRateValues.find((preset) => preset.value === value)
  );
}

export function getBreakTimeValue(breakTime: string, timeUnit: string): string {
  // 100-5000 is the allowed range for milliseconds
  if (timeUnit === "ms") {
    if (Number(breakTime) > 5000) return `5000${timeUnit}`;
    if (Number(breakTime) < 100) return `100${timeUnit}`;
    return `${breakTime}${timeUnit}`;
  }
  // timeUnit === "s" - 1-5 is the allowed range for seconds
  if (Number(breakTime) > 5) return `5${timeUnit}`;
  if (Number(breakTime) < 1) return `1${timeUnit}`;
  return `${breakTime}${timeUnit}`;
}

export function extractTimeValueAndUnit(timeString: string): {
  value: string;
  unit: string;
} {
  const result = { value: "", unit: "ms" };

  if (
    ["none", "x-weak", "weak", "medium", "strong", "x-strong"].includes(
      timeString
    )
  ) {
    return { value: timeString, unit: "" };
  }

  const match = timeString.match(/^(\d+\.?\d*)([a-z]+)$/i);
  if (match) {
    result.value = match[1];
    result.unit = match[2].toLowerCase();
  }

  return result;
}

export function convertContourValues(
  contourValues: SelectedContourValueProps[]
): string {
  let result = "";
  contourValues.forEach((contourValue, index) => {
    if (index === 0 || index === 4) return; // ignore the first and last entries
    // check if contour value or time is empty
    if (contourValue.data.value < 0) {
      result += `(${contourValue.data.time}%,${contourValue.data.value}st)`;
    } else {
      result += `(${contourValue.data.time}%,+${contourValue.data.value}st)`;
    }
  });
  return result;
}

export function getSignedPitchValue(value: string): string {
  let pitch = value;
  // if user enters a positive sign, do not change anything
  const regex = /^\+\d+$/;
  if (Number(pitch) >= 0 && !regex.test(pitch)) pitch = `+${pitch}`;
  return pitch;
}

// Returns true if it is invalid
export function checkInvalidNestedTag(
  editorRef: React.RefObject<EditorRef | null>,
  tagToCheck: string
): CheckInvalidNestedTagsProps {
  const editor = editorRef.current?.editor;
  if (!editor)
    return {
      shouldShowError: true,
      isWrappedWithSameTag: false,
      isWrappedWithSayAsTag: false,
    };

  const regexClosingTag = /^<\/[^>]+>$/;
  const regexSelfClosingTag = /^<[^>]+\/>$/;
  let selectedNode = editor.selection.getNode();

  const selectedNodeTextContent = selectedNode?.textContent;
  // If selectedNode has a textNode of closing regex tag, change selectedNode to its parent node
  // This logic is to handle if user clicks next to the closing tag
  if (regexClosingTag.test(selectedNodeTextContent || "")) {
    // change selectedNode to its parent node
    selectedNode = selectedNode?.parentNode?.parentNode as HTMLElement;
  }
  const ssmlTagAttribute = selectedNode?.getAttribute("data-ssmltag");

  const range = editor.selection.getRng();
  let surroundingNode = range.commonAncestorContainer.parentNode
    ?.parentNode as Element;

  if (regexClosingTag.test(selectedNodeTextContent || "")) {
    // change selectedNode to its parent node
    surroundingNode = surroundingNode.parentNode as Element;
  }

  let isWrappedWithSameTag = false;
  let isWrappedWithSayAsTag = false;
  let ssmlTagAttributeExists = false;

  // logic is that, if tagToCheck is say-as (means the call is from say-as menu),
  // then for any ssml-tag attribute, it should show error if it is wrapped with say-as tag
  // ssmlTagAttributeExists is to check if selected text has any other ssml-tag attribute
  if (ssmlTagAttribute) {
    ssmlTagAttributeExists = true;
  }

  if (surroundingNode) {
    if (surroundingNode.nodeType === 1) {
      isWrappedWithSameTag =
        surroundingNode?.getAttribute("data-ssmltag") === tagToCheck ||
        ssmlTagAttribute === tagToCheck;
      isWrappedWithSayAsTag =
        surroundingNode?.getAttribute("data-ssmltag") === "sayas" ||
        (tagToCheck === "sayas" && ssmlTagAttributeExists) ||
        regexSelfClosingTag.test(selectedNodeTextContent || "");
    }
  }
  if (
    ssmlTagAttribute === tagToCheck ||
    (tagToCheck === "sayas" && ssmlTagAttributeExists) ||
    isWrappedWithSameTag ||
    isWrappedWithSayAsTag
  )
    return {
      shouldShowError: true,
      isWrappedWithSameTag,
      isWrappedWithSayAsTag,
    };
  return {
    shouldShowError: false,
    isWrappedWithSameTag: false,
    isWrappedWithSayAsTag: false,
  };
}

// Returns true if any of the prosody attributes are already wrapped around the selected text
export function checkInvalidNestedTagForProsody(
  editorRef: React.RefObject<EditorRef | null>
): CheckInvalidNestedTagsProps {
  const editor = editorRef.current?.editor;
  if (!editor)
    return {
      shouldShowError: true,
      isWrappedWithSameTag: false,
      isWrappedWithSayAsTag: false,
    };

  const regexClosingTag = /^<\/[^>]+>$/;
  let selectedNode = editor.selection.getNode();

  const selectedNodeTextContent = selectedNode?.textContent;
  if (regexClosingTag.test(selectedNodeTextContent || "")) {
    // change selectedNode to its parent node
    selectedNode = selectedNode?.parentNode?.parentNode as HTMLElement;
  }

  const ssmlTagAttribute = selectedNode?.getAttribute("data-ssmltag");

  // check if ssmlTagAttribute matches any of the prosody attributes
  const ssmlTagAttributeExists =
    prosodyAttributes.filter((attr) => attr === ssmlTagAttribute).length > 0;

  const range = editor.selection.getRng();
  let surroundingNode = range.commonAncestorContainer.parentNode
    ?.parentNode as Element;

  if (regexClosingTag.test(selectedNodeTextContent || "")) {
    // change selectedNode to its parent node
    surroundingNode = surroundingNode.parentNode as Element;
  }

  let isTextNodeWrapped = false;
  let isWrappedWithSayAsTag = false;

  if (surroundingNode) {
    if (surroundingNode.nodeType === 1) {
      isTextNodeWrapped =
        prosodyAttributes.filter((attr) =>
          surroundingNode?.getAttribute("data-ssmltag")?.includes(`${attr}`)
        ).length > 0;
      isWrappedWithSayAsTag =
        surroundingNode?.getAttribute("data-ssmltag") === "sayas";
    }
  }
  if (ssmlTagAttributeExists || isTextNodeWrapped || isWrappedWithSayAsTag)
    return {
      shouldShowError: true,
      isWrappedWithSameTag: isTextNodeWrapped || ssmlTagAttributeExists,
      isWrappedWithSayAsTag,
    };
  return {
    shouldShowError: false,
    isWrappedWithSameTag: false,
    isWrappedWithSayAsTag: false,
  };
}

export function separateTagsInItsOwnSpanTag(
  editorRef: React.RefObject<EditorRef | null>
): void {
  if (editorRef.current) {
    const { editor } = editorRef.current;

    if (editor) {
      // gives current cursor position in editor
      const range = editor.selection.getRng();
      // gives parent node of the current cursor position
      const currentNode = range.startContainer as Element;
      const parentNode = range.startContainer.parentNode as Element;
      const grandParentNode = parentNode.parentNode as Element;

      const beginTagRegex = /<[a-zA-Z="]+([^<]+)>/g;
      const endTagRegex = /<\/[a-z]{1,}([^<]+)>/g;

      const currentNodeChildren = currentNode.children;
      if (
        currentNode.nodeName !== "P" &&
        parentNode.nodeName !== "P" &&
        currentNodeChildren
      ) {
        const currentNodeChildrenArray = Array.from(currentNodeChildren);
        currentNodeChildrenArray.forEach((child, index) => {
          if (child.getAttribute("data-ssmltag")) {
            if (beginTagRegex.test(child.previousSibling?.textContent || "")) {
              // moved children still should be part of the parent node
              for (let i = index; i < currentNodeChildren.length; i += 1) {
                parentNode.insertBefore(
                  currentNodeChildren[i],
                  currentNode.nextSibling
                );
              }
            }
            if (endTagRegex.test(child.previousSibling?.textContent || "")) {
              for (let i = index; i < currentNodeChildren.length; i += 1) {
                grandParentNode.insertBefore(
                  currentNodeChildren[i],
                  parentNode.nextSibling
                );
              }
            }
          }
        });
      }
    }
  }
}

// Deals with the logic if a tag is added right at the end of another start tag or end tag
export function addHtmlContent(
  editorRef: React.RefObject<EditorRef | null>,
  htmlContent: string
): void {
  if (editorRef.current) {
    const { editor } = editorRef.current;

    if (editor) {
      editor.execCommand("InsertHTML", false, htmlContent);
    }
  }
}

// Check if text matches SSML tags
export function checkTextMatchesWithTags(match: string): boolean {
  return ssmlTagsAndAttributes.some(
    (tag) =>
      (match.startsWith(`<${tag.name} ${tag.attr}=`) && match.endsWith(">")) ||
      match.startsWith(`</${tag.name}>`)
  );
}

export const updateSayAsTag = (
  editor: EditorRef["editor"],
  interpretAs: string,
  lang?: string,
  format?: string,
  detail?: string
) => {
  if (!editor) return false;

  const node = editor.selection.getNode();
  const span = node.closest("span[data-ssml]");
  if (!span) return false;

  const currentDataSSML = span.getAttribute("data-ssml");
  if (!currentDataSSML) return false;

  const [tagWithId, currentValues] = currentDataSSML.split("=");

  // Parse existing values to preserve format and detail if not provided
  const currentParts = currentValues
    ? currentValues.split(",").map((p) => p.trim())
    : [];

  const newParts = [interpretAs];

  if (lang && lang !== "automatic") {
    newParts.push(lang);
  } else if (currentParts.length > 2 || format || detail) {
    newParts.push("");
  }

  const existingFormat = format !== undefined ? format : currentParts[2] || "";
  if (existingFormat || detail) {
    newParts.push(existingFormat);
  }

  const existingDetail = detail !== undefined ? detail : currentParts[3] || "";
  if (existingDetail) {
    newParts.push(existingDetail);
  }

  const newDataSSML = `${tagWithId}=${newParts.join(", ")}`;

  span.setAttribute("data-ssml", newDataSSML);
  editor.fire("change");
  return true;
};

export const updateExistingProsodyTag = (
  editor: EditorRef["editor"],
  attributes: string[]
) => {
  if (!editor) return false;

  const selectedNode = editor.selection.getNode();
  
  const prosodySelector = [
    `span[data-ssml*="${SSMLTag.Volume}-"]`,
    `span[data-ssml*="${SSMLTag.Rate}-"]`,
    `span[data-ssml*="${SSMLTag.Duration}-"]`,
    `span[data-ssml*="${SSMLTag.Pitch}-"]`,
    `span[data-ssml*="${SSMLTag.Contour}-"]`
  ].join(",");

  const prosodyElement = selectedNode.closest(prosodySelector) as HTMLElement;

  if (!prosodyElement) return false;

  if (attributes.length > 0) {
    prosodyElement.setAttribute("data-ssml", attributes.join(","));
  } else {
    editor.dom.remove(prosodyElement, true);
  }

  return true;
};
